import {ColDef, GetRowIdParams, GridApi, IServerSideGetRowsParams} from "ag-grid-enterprise";
import {Button, Chip, Grid, IconButton, MenuItem, Paper, Popover, Select, Snackbar, Alert, Stack, TextField, Tooltip, useTheme} from "@mui/material";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {MoreVert, Edit, ContentCopy, Delete, SearchRounded, Add, DescriptionRounded, ReplayRounded, Close} from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {useTranslation} from "react-i18next";
import {useAgGridLocale, useHasScope} from "../../../../config/globalConfig";
import {AgGridReact} from "ag-grid-react";
import {useNavigate, useLocation} from "react-router-dom";
import EmptyState from "../../../../util/component/EmptyState";
import SectionHeader from "../../../../util/component/SectionHeader";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {getTemplates, deleteTemplate, deleteTemplates, duplicateTemplate} from "../../api/templateApi";
import {Template, TemplateType, getAllTenantIds, extractApiError, getErrorMessage} from "../../types";
import DuplicateTemplateDialog from "../../components/DuplicateTemplateDialog";
import DeleteTemplateDialog from "../../components/DeleteTemplateDialog";
import ResultDialog from "../../components/ResultDialog";
import CustomLoader from "../../../../util/component/CustomLoader";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";

dayjs.extend(utc);

const formatDate = (date: string) => dayjs.utc(date).local().format('DD-MM-YYYY HH:mm');

interface DatasourceFilters {
    type: string;
    nameTokens: string[];
    nameOperator: string;
    tenantId: string[];
}

function createDatasource(filters: DatasourceFilters, api: GridApi, setLoading: (v: boolean) => void) {
    return {
        getRows: async (params: IServerSideGetRowsParams) => {
            setLoading(true);
            const startRow = params.request.startRow ?? 0;
            const endRow = params.request.endRow ?? 10;
            const pageSize = endRow - startRow;
            const page = pageSize > 0 ? Math.floor(startRow / pageSize) : 0;
            const sortBy = params.request.sortModel.length > 0
                ? params.request.sortModel[0].colId : "updatedAt";
            const sortDirection = params.request.sortModel.length > 0
                ? params.request.sortModel[0].sort : "desc";
            try {
                const data = await getTemplates({
                    tenantId: filters.tenantId,
                    bodyType: "html",
                    page,
                    size: pageSize,
                    sortBy,
                    sortDirection,
                    type: filters.type || undefined,
                    nameTokens: filters.nameTokens.length > 0 ? filters.nameTokens : undefined,
                    nameOperator: filters.nameTokens.length > 1 ? filters.nameOperator : undefined,
                });
                console.log('[TemplatesList] raw API response:', JSON.stringify(data));
                const content = Array.isArray(data?.content) ? data.content : [];
                const total = typeof data?.totalElements === 'number' ? data.totalElements : 0;
                params.success({rowData: content, rowCount: total});
                if (total === 0) {
                    api.showNoRowsOverlay();
                } else {
                    api.hideOverlay();
                }
                setLoading(false);
            } catch (e: any) {
                console.error('[TemplatesList] getTemplates FAILED:', e?.response?.status, e?.response?.data, e);
                params.success({rowData: [], rowCount: 0});
                api.showNoRowsOverlay();
                setLoading(false);
            }
        }
    };
}

const CHANNEL_TYPES: TemplateType[] = ['EMAIL', 'SMS', 'PUSH'];

const channelColor: Record<TemplateType, "primary" | "secondary" | "success"> = {
    EMAIL: "primary",
    SMS: "secondary",
    PUSH: "success",
};

const TemplatesList = () => {
    const {t} = useTranslation(['templates']);
    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const {globalData} = useContext(GlobalContext);
    const user = globalData?.user;
    const username: string = user?.username || 'unknown';
    const tenantIds = getAllTenantIds();

    const [typeFilter, setTypeFilter] = useState<string>("");
    const [nameSearch, setNameSearch] = useState<string>("");
    const [searchTokens, setSearchTokens] = useState<string[]>([]);
    const [nameOperator, setNameOperator] = useState<string>("AND");
    const [searchDuplicateError, setSearchDuplicateError] = useState(false);

    const canView = useHasScope("SCOPE_NOTIFICATION_HUB_TEMPLATE_VIEW");
    const canEdit = useHasScope("SCOPE_NOTIFICATION_HUB_TEMPLATE_EDIT");
    const canCreate = useHasScope("SCOPE_NOTIFICATION_HUB_TEMPLATE_CREATE");
    const canDelete = useHasScope("SCOPE_NOTIFICATION_HUB_TEMPLATE_DELETE");

    const [duplicateOpen, setDuplicateOpen] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [duplicateOriginal, setDuplicateOriginal] = useState<Template | null>(null);
    const [snackError, setSnackError] = useState<string | null>(null);

    // Show snackbar from navigation state (e.g. template not found redirect)
    useEffect(() => {
        const state = location.state as { snackError?: string } | null;
        if (state?.snackError) {
            setSnackError(state.snackError);
            // Clear the state so it doesn't show again on refresh
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname]);
    const [selectionCount, setSelectionCount] = useState(0);
    const selectionCountRef = useRef(0);
    const selectedNamesRef = useRef<Set<string>>(new Set());
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteTargets, setDeleteTargets] = useState<Template[]>([]);
    const [deleteResultOpen, setDeleteResultOpen] = useState(false);
    const [deletedCount, setDeletedCount] = useState(0);
    const [duplicateResultOpen, setDuplicateResultOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const scrollTableToTop = useCallback(() => {
        requestAnimationFrame(() => {
            window.scrollTo({top: 0, behavior: 'auto'});
        });
    }, []);

    const RowActionsMenu = ({row}: { row: Template }) => {
        const theme = useTheme();
        const accentColor = theme.palette.accent.main;
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);
        const showMenu = canCreate || canDelete;

        const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(e.currentTarget);
            const rowEl = e.currentTarget.closest('[row-id]') as HTMLElement | null;
            if (rowEl) rowEl.style.backgroundColor = '#BDE2F0';
        };
        const handleMenuClose = () => {
            if (anchorEl) {
                const rowEl = anchorEl.closest('[row-id]') as HTMLElement | null;
                if (rowEl) rowEl.style.backgroundColor = '';
            }
            setAnchorEl(null);
        };
        // Selezione: 0 -> tutte attive; 1 -> solo la riga selezionata; 2+ -> tutte disattivate
        const count = selectionCountRef.current;
        const isThisRowSelected = selectedNamesRef.current.has(row.name);
        const actionsDisabled = count > 1 || (count === 1 && !isThisRowSelected);
        const iconColor = actionsDisabled ? 'action.disabled' : accentColor;
        return (
            <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end">
                {canEdit && (
                    <Tooltip arrow title={t('listActions.edit')}>
                        <span>
                            <IconButton size="small" disabled={actionsDisabled} onClick={() => window.open(`/notification-hub/edit/${row.name}`, '_blank')} sx={{color: iconColor}}>
                                <Edit/>
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {canView && (
                    <Tooltip arrow title={t('listActions.view')}>
                        <span>
                            <IconButton size="small" disabled={actionsDisabled} onClick={() => window.open(`/notification-hub/view/${row.name}`, '_blank')} sx={{color: iconColor}}>
                                <DescriptionRounded/>
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {showMenu && (
                    <>
                        <IconButton size="small" disabled={actionsDisabled} onClick={handleMenuOpen} sx={{color: iconColor}}>
                            <MoreVert/>
                        </IconButton>
                        <Popover
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleMenuClose}
                            anchorOrigin={{vertical: 'top', horizontal: 'left'}}
                            sx={{marginLeft: -1}}
                        >
                            <Stack spacing={0.5} sx={{padding: '0.3em'}}>
                                {canCreate && (
                                    <Tooltip arrow title={t('listActions.duplicate')} placement="right-start">
                                        <IconButton onClick={() => { handleMenuClose(); setDuplicateOriginal(row); setDuplicateOpen(true); }} sx={{color: accentColor}}>
                                            <ContentCopy/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {canDelete && (
                                    <Tooltip arrow title={t('listActions.delete')} placement="right-start">
                                        <IconButton onClick={() => { handleMenuClose(); setDeleteTargets([row]); setDeleteOpen(true); }} sx={{color: accentColor}}>
                                            <Delete/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Popover>
                    </>
                )}
            </Stack>
        );
    };

    const triggerSearch = (tokens: string[], operator: string) => {
        const api = gridRef.current?.api;
        if (!api) return;
        api.setServerSideDatasource(createDatasource({type: typeFilter, nameTokens: tokens, nameOperator: operator, tenantId: tenantIds}, api, setLoading));
    };

    const handleSearch = () => {
        const trimmed = nameSearch.trim();
        if (!trimmed) {
            triggerSearch(searchTokens, nameOperator);
            return;
        }
        if (searchTokens.length >= 5) {
            setSnackError(t('listFilters.maxTokensReached'));
            return;
        }
        if (searchTokens.some(t => t.toLowerCase() === trimmed.toLowerCase())) {
            setNameSearch("");
            setSearchDuplicateError(true);
            return;
        }
        const newTokens = [...searchTokens, trimmed];
        setSearchTokens(newTokens);
        setNameSearch("");
        triggerSearch(newTokens, nameOperator);
    };

    const handleRemoveToken = (index: number) => {
        const newTokens = searchTokens.filter((_, i) => i !== index);
        setSearchTokens(newTokens);
        triggerSearch(newTokens, nameOperator);
    };

    const handleOperatorChange = (newOperator: string) => {
        setNameOperator(newOperator);
        if (searchTokens.length > 1) {
            triggerSearch(searchTokens, newOperator);
        }
    };

    const handleReset = () => {
        setNameSearch("");
        setSearchTokens([]);
        setNameOperator("AND");
        setTypeFilter("");
        const api = gridRef.current?.api;
        if (!api) return;
        api.setServerSideDatasource(createDatasource({type: "", nameTokens: [], nameOperator: "AND", tenantId: tenantIds}, api, setLoading));
    };

    const getSelectedRows = (): Template[] => {
        return gridRef.current?.api?.getSelectedRows() ?? [];
    };

    const handleDuplicateConfirm = async (newName: string) => {
        if (!duplicateOriginal) return;
        setDuplicating(true);
        try {
            await duplicateTemplate(duplicateOriginal.name, newName, username);
            setDuplicateOpen(false);
            setDuplicateOriginal(null);
            setDuplicateResultOpen(true);
        } catch (e: any) {
            const apiError = extractApiError(e);
            if (apiError) {
                const errorMsg = getErrorMessage(apiError.code, t);
                setSnackError(errorMsg);
            } else {
                setSnackError(t('detailErrors.duplicate'));
            }
        } finally {
            setDuplicating(false);
        }
    };

    const handleDelete = async () => {
        const targets = deleteTargets.length > 0 ? deleteTargets : getSelectedRows();
        if (targets.length === 0) return;
        setDeleting(true);
        try {
            if (targets.length === 1) {
                await deleteTemplate(targets[0].name, username);
            } else {
                await deleteTemplates(targets.map(t => t.name), username);
            }
            setDeleteOpen(false);
            setDeleteTargets([]);
            gridRef.current?.api?.deselectAll();
            setDeletedCount(targets.length);
            setDeleteResultOpen(true);
        } catch (e) {
            // On error, keep state unchanged
        } finally {
            setDeleting(false);
        }
    };

    const visibleRowActions = [canEdit, canView, canCreate || canDelete].filter(Boolean).length;
    const actionColumnWidth = Math.max(72, visibleRowActions * 42 + 24);

    const columns: ColDef<Template>[] = [
        {field: "name", headerName: t('listColumns.name'), unSortIcon: true, flex: 2,
            headerCheckboxSelection: canDelete, checkboxSelection: canDelete,
            cellStyle: {textAlign: 'left'}},
        {field: "type", headerName: t('listColumns.channelType'), unSortIcon: true, flex: 1,
            cellRenderer: (params: any) => params.value ? <Chip label={params.value} color={channelColor[params.value as TemplateType] || "default"} size="small"/> : ''},
        {field: "createdAt", headerName: t('listColumns.createdDate'), unSortIcon: true, flex: 1,
            valueFormatter: (params) => params.value ? formatDate(params.value) : ''},
        {field: "updatedAt", headerName: t('listColumns.lastModified'), unSortIcon: true, flex: 1,
            valueFormatter: (params) => params.value ? formatDate(params.value) : ''},
        {
            field: "_actions",
            headerName: '',
            width: actionColumnWidth,
            maxWidth: actionColumnWidth,
            minWidth: actionColumnWidth,
            sortable: false,
            resizable: false,
            suppressMenu: true,
            cellStyle: {display: 'flex', justifyContent: 'flex-end', alignItems: 'center'},
            cellRenderer: (params: any) => <RowActionsMenu row={params.data}/>,
        },
    ];

    const [selectAll, setSelectAll] = useState(false);

    const handleSelectAll = () => {
        const api = gridRef.current?.api;
        if (!api) return;
        if (selectAll) {
            api.deselectAll();
        } else {
            api.forEachNode((node: any) => node.setSelected(true));
        }
        setSelectAll(!selectAll);
    };

    const toolbar = {
        renderActions: () => (
            <>
                {canDelete && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleSelectAll}
                    >
                        {selectAll ? t('listActions.deselectAll') : t('listActions.selectAll')}
                    </Button>
                )}
                {canDelete && (
                    <Button
                        size="small"
                        variant="contained"
                        color="accent"
                        startIcon={<Delete/>}
                        disabled={selectionCount === 0}
                        onClick={() => { setDeleteTargets(getSelectedRows()); setDeleteOpen(true); }}
                    >
                        {t('listActions.delete')} {selectionCount > 0 ? `(${selectionCount})` : ''}
                    </Button>
                )}
                {canCreate && (
                    <IconButton
                        size="small"
                        onClick={() => window.open("/notification-hub/create", '_blank')}
                        sx={{color: 'accent.main'}}
                    >
                        <Add/>
                    </IconButton>
                )}
            </>
        )
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    return (
        <>
            <SectionHeader title={t('listHeader.title')} subtitle={t('listHeader.subTitle')}/>
            <Paper elevation={1} sx={{padding: "16px", margin: "18px"}}>
                <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                label={searchDuplicateError ? t('listFilters.duplicateToken') : t('listFilters.name')}
                                value={nameSearch}
                                onChange={(e) => { setNameSearch(e.target.value); setSearchDuplicateError(false); }}
                                error={searchDuplicateError}
                                InputLabelProps={searchDuplicateError ? {shrink: true} : undefined}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                label={t('listFilters.channelType')}
                                select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                            >
                                <MenuItem value="">{t('listFilters.all')}</MenuItem>
                                {CHANNEL_TYPES.map((ct) => (
                                    <MenuItem key={ct} value={ct}>{ct}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item container xs={6} justifyContent="flex-end" alignItems="center">
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Button variant="text" size="smallPlus" color="accent"
                                        onClick={handleReset}>
                                    <ReplayRounded/>
                                    {t('listFilters.reset')}
                                </Button>
                                <Button type="submit" size="icon" color="accent"><SearchRounded/></Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </form>
                {searchTokens.length > 0 && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 1.5, flexWrap: 'wrap', gap: 0.5}}>
                        {searchTokens.map((token, index) => (
                            <Chip
                                key={index}
                                label={token}
                                size="small"
                                color="accent"
                                variant="outlined"
                                onDelete={() => handleRemoveToken(index)}
                                deleteIcon={<Close sx={{fontSize: 16}}/>}
                            />
                        ))}
                        {searchTokens.length > 1 && (
                            <Select
                                size="small"
                                value={nameOperator}
                                onChange={(e) => handleOperatorChange(e.target.value)}
                                sx={{minWidth: 70, height: 28, fontSize: '0.75rem', ml: 1}}
                            >
                                <MenuItem value="AND">AND</MenuItem>
                                <MenuItem value="OR">OR</MenuItem>
                            </Select>
                        )}
                    </Stack>
                )}
            </Paper>
            <Grid container ref={tableContainerRef}>
                <Grid item xs={12} sx={{padding: "16px"}}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        columnDefs={columns}
                        defaultColDef={{sortable: true, resizable: true}}
                        suppressMultiSort
                        getRowId={(params: GetRowIdParams<Template>) => params.data.name}
                        rowSelection="multiple"
                        suppressRowClickSelection
                        onSelectionChanged={(event) => {
                            const selectedRows = event.api.getSelectedRows();
                            const selected = selectedRows.length;
                            selectionCountRef.current = selected;
                            selectedNamesRef.current = new Set(selectedRows.map((r: Template) => r.name));
                            setSelectionCount(selected);
                            let total = 0;
                            event.api.forEachNode(() => total++);
                            setSelectAll(total > 0 && selected === total);
                            // Aggiorna le azioni di riga per riflettere lo stato disabilitato/abilitato
                            event.api.refreshCells({columns: ['_actions'], force: true});
                        }}
                        onPaginationChanged={(event: any) => {
                            event.api.deselectAll();
                            selectionCountRef.current = 0;
                            selectedNamesRef.current = new Set();
                            setSelectionCount(0);
                            setSelectAll(false);
                            scrollTableToTop();
                        }}
                        toolbar={toolbar}
                        pagination
                        localeText={agGridLocale}
                        rowModelType="serverSide"
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        noRowsOverlayComponentParams={{title: t('listFilters.noResults')}}
                        onGridReady={(params) => {
                            // Ordinamento iniziale: Ultima Modifica desc (applicato una sola volta,
                            // cosi' l'operatore puo' poi cambiarlo liberamente su qualsiasi colonna)
                            params.columnApi.applyColumnState({
                                state: [{colId: 'updatedAt', sort: 'desc'}],
                                defaultState: {sort: null},
                            });
                            params.api.setServerSideDatasource(createDatasource({type: typeFilter, nameTokens: searchTokens, nameOperator, tenantId: tenantIds}, params.api, setLoading));
                        }}
                    />
                </Grid>
            </Grid>

            <DuplicateTemplateDialog
                open={duplicateOpen}
                originalName={duplicateOriginal?.name || ""}
                templateType={duplicateOriginal?.type}
                onClose={() => { setDuplicateOpen(false); setDuplicateOriginal(null); }}
                onConfirm={handleDuplicateConfirm}
                loading={duplicating}
            />

            <DeleteTemplateDialog
                open={deleteOpen}
                templateNames={deleteTargets.map(t => t.name)}
                onClose={() => { setDeleteOpen(false); setDeleteTargets([]); }}
                onConfirm={handleDelete}
                loading={deleting}
            />

            <ResultDialog
                open={deleteResultOpen}
                success
                title={t('deleteResultDialog.successTitle')}
                message={deletedCount > 1
                    ? t('deleteResultDialog.successMessageMultiple', {count: deletedCount})
                    : t('deleteResultDialog.successMessageSingle')}
                onClose={() => { setDeleteResultOpen(false); handleSearch(); }}
            />

            <ResultDialog
                open={duplicateResultOpen}
                success
                title={t('duplicateResultDialog.successTitle')}
                message={t('duplicateResultDialog.successMessage')}
                onClose={() => { setDuplicateResultOpen(false); handleSearch(); }}
            />

            <CustomLoader loading={loading}/>

            <Snackbar open={!!snackError} autoHideDuration={5000} onClose={() => setSnackError(null)} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Alert severity="error" onClose={() => setSnackError(null)} variant="filled">{snackError}</Alert>
            </Snackbar>
        </>
    );
};

export default TemplatesList;
